// ---------------------------------------------------------------------------
// YouAreYou (uru) — LoginPage
//
// Handles all authentication entry points:
//   1. Magic link token exchange (?token=ml:...)
//   2. Passkey (WebAuthn) login via phone number
//   3. SMS OTP fallback login
//
// Design notes:
//   - No mockup exists for the login page yet, so this follows the design
//     system tokens (Public Sans, primary blue, light background).
//   - The page auto-detects a magic link token in the URL query params
//     and exchanges it immediately on mount.
//   - For returning users: phone number input → passkey attempt → SMS fallback.
//
// API references (docs/api.md):
//   - POST /api/auth/magic/login
//   - POST /api/auth/passkeys/login/begin → /complete
//   - POST /api/auth/sms/login → /verify
//   - POST /api/phonehub/number/normalize
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { normalizePhone } from "@/api/endpoints";
import { ApiRequestError, NetworkError } from "@/api/client";
import BrandName from "@/components/ui/BrandName";
import BrandMark from "@/components/ui/BrandMark";
import LogoSpinner from "@/components/ui/LogoSpinner";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type LoginStep =
  | "phone" // Enter phone number
  | "magic-link" // Exchanging magic link token (auto, no UI input needed)
  | "passkey" // Attempting passkey authentication
  | "sms-sent" // SMS OTP sent, waiting for code input
  | "sms-verify"; // Verifying SMS OTP code

// ---------------------------------------------------------------------------
// Icon helper
// ---------------------------------------------------------------------------

function Icon({ name, className = "" }: { name: string; className?: string }) {
  return (
    <span className={`material-symbols-outlined select-none ${className}`}>
      {name}
    </span>
  );
}

// ---------------------------------------------------------------------------
// LoginPage Component
// ---------------------------------------------------------------------------

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    status,
    loginWithMagicLink,
    loginWithPasskey,
    sendSmsOtp,
    verifySmsOtp,
  } = useAuth();

  // ---- State ----

  const [step, setStep] = useState<LoginStep>("phone");
  const [phone, setPhone] = useState("");
  const [normalizedPhone, setNormalizedPhone] = useState("");
  const [smsCode, setSmsCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Show a success banner when arriving from registration (MojoVerify redirects here with ?registered=true)
  const justRegistered = searchParams.get("registered") === "true";

  // Guard against double magic link exchange in StrictMode
  const magicLinkProcessedRef = useRef(false);

  // Where to redirect after successful login — default depends on stored mode preference
  const storedMode = localStorage.getItem("uru_app_mode");
  const redirectTo =
    (location.state as { from?: { pathname: string } })?.from?.pathname ??
    (storedMode === "advanced" ? "/dashboard" : "/quick-check");

  // ---- Redirect if already authenticated ----

  useEffect(() => {
    if (status === "authenticated") {
      navigate(redirectTo, { replace: true });
    }
  }, [status, navigate, redirectTo]);

  // ---- Auto-detect magic link token in URL ----
  // @deprecated Backend no longer sends ml: tokens — first login is now SMS OTP.
  // Kept for transition: tokens already in transit when backend was updated still work.

  useEffect(() => {
    const token = searchParams.get("token");
    if (!token || !token.startsWith("ml:") || magicLinkProcessedRef.current)
      return;

    magicLinkProcessedRef.current = true;
    setStep("magic-link");
    setLoading(true);
    setError(null);

    // Remove token from URL to prevent re-processing on refresh
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("token");
    setSearchParams(newParams, { replace: true });

    loginWithMagicLink(token)
      .then(() => {
        navigate(redirectTo, { replace: true });
      })
      .catch((err) => {
        setError(
          err instanceof ApiRequestError
            ? err.message
            : "Magic link is invalid or expired. Please request a new one.",
        );
        setStep("phone");
        setLoading(false);
      });
  }, [searchParams, setSearchParams, loginWithMagicLink, navigate, redirectTo]);

  // ---- Phone Normalization + Passkey Login ----

  const handlePhoneSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!phone.trim()) return;

      setLoading(true);
      setError(null);

      try {
        // Normalize phone number to E.164
        const { phone_number } = await normalizePhone({ phone_number: phone });
        setNormalizedPhone(phone_number);

        // Try passkey first
        setStep("passkey");

        try {
          await loginWithPasskey(phone_number);
          navigate(redirectTo, { replace: true });
          return;
        } catch {
          // Passkey failed (not registered, user cancelled, WebAuthn not available).
          // Fall through to SMS OTP.
        }

        // Fall back to SMS OTP
        setStep("sms-sent");
        await sendSmsOtp(phone_number);
        setLoading(false);
      } catch (err) {
        if (err instanceof ApiRequestError) {
          setError(err.message);
        } else if (err instanceof NetworkError) {
          setError("Unable to connect. Please check your internet connection.");
        } else {
          setError("Something went wrong. Please try again.");
        }
        setStep("phone");
        setLoading(false);
      }
    },
    [phone, loginWithPasskey, sendSmsOtp, navigate, redirectTo],
  );

  // ---- SMS OTP Verification ----

  const handleSmsVerify = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (smsCode.length < 6) return;

      setLoading(true);
      setError(null);
      setStep("sms-verify");

      try {
        await verifySmsOtp(normalizedPhone, smsCode);
        navigate(redirectTo, { replace: true });
      } catch (err) {
        setError(
          err instanceof ApiRequestError
            ? err.message
            : "Verification failed. Please check the code and try again.",
        );
        setStep("sms-sent");
        setLoading(false);
      }
    },
    [smsCode, normalizedPhone, verifySmsOtp, navigate, redirectTo],
  );

  // ---- Resend SMS ----

  const handleResendSms = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      await sendSmsOtp(normalizedPhone);
      setError(null);
      setSmsCode("");
    } catch {
      setError("Failed to resend code. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [normalizedPhone, sendSmsOtp]);

  // ---- Use SMS fallback explicitly ----

  const handleUseSms = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      await sendSmsOtp(normalizedPhone);
      setStep("sms-sent");
    } catch {
      setError("Failed to send SMS code. Please try again.");
      setStep("phone");
    } finally {
      setLoading(false);
    }
  }, [normalizedPhone, sendSmsOtp]);

  // ---- Render ----

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center bg-[#fef0f0] px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="mb-8 flex flex-col items-center">
          <div className="mb-4">
            <BrandMark className="h-16 w-auto" />
          </div>
          <h1 className="text-2xl font-bold text-text-primary">
            Welcome to <BrandName />
          </h1>
          <p className="mt-1 text-sm text-text-secondary">
            Verified identity you can trust
          </p>
        </div>

        {/* Post-registration success banner */}
        {justRegistered && (
          <div className="mb-4 flex items-start gap-2 rounded-lg bg-green-50 p-4 text-sm text-green-800" role="status">
            <Icon name="check_circle" className="mt-0.5 shrink-0 text-lg text-green-600" />
            <div>
              <p className="font-semibold">Verification complete!</p>
              <p className="mt-0.5 text-green-700">Enter your phone number to sign in to your new account.</p>
            </div>
          </div>
        )}

        {/* Card */}
        <div className="rounded-2xl bg-white p-6 shadow-[0px_4px_20px_rgba(26,31,43,0.06)]">
          {/* Error banner */}
          {error && (
            <div
              className="mb-4 flex items-start gap-2 rounded-lg bg-red-50 p-3 text-sm text-error"
              role="alert"
            >
              <Icon name="error" className="mt-0.5 text-lg shrink-0" />
              <p>{error}</p>
            </div>
          )}

          {/* ---- Step: Magic Link (auto-processing) ---- */}
          {step === "magic-link" && (
            <div className="flex flex-col items-center py-8">
              <LogoSpinner size="md" label="Signing you in…" />
            </div>
          )}

          {/* ---- Step: Phone Number Input ---- */}
          {step === "phone" && (
            <form onSubmit={handlePhoneSubmit} className="flex flex-col gap-4">
              <div>
                <label
                  htmlFor="phone"
                  className="mb-1 block text-sm font-medium text-text-primary"
                >
                  Phone number
                </label>
                <div className="relative">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <Icon name="phone" className="text-lg text-text-muted" />
                  </div>
                  <input
                    id="phone"
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    placeholder="(555) 123-4567"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    disabled={loading}
                    required
                    className="w-full rounded-xl border border-[#f0e0e0] py-2.5 pl-10 pr-4 text-sm text-text-primary placeholder:text-text-muted focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 focus:outline-none disabled:bg-gray-50 disabled:text-text-muted"
                  />
                </div>
                <p className="mt-1 text-xs text-text-muted">
                  We&apos;ll use Face ID, Touch ID, or send a code to sign you
                  in.
                </p>
              </div>

              <button
                type="submit"
                disabled={loading || !phone.trim()}
                className="flex items-center justify-center gap-2 rounded-xl bg-primary-500 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600 focus:ring-2 focus:ring-primary-500/20 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                    Signing in…
                  </>
                ) : (
                  <>
                    Continue
                    <Icon name="arrow_forward" className="text-lg" />
                  </>
                )}
              </button>
            </form>
          )}

          {/* ---- Step: Passkey Attempt ---- */}
          {step === "passkey" && (
            <div className="flex flex-col items-center gap-4 py-8">
              <LogoSpinner size="md" label="Authenticating with Face ID / Touch ID…" />
              <button
                onClick={handleUseSms}
                disabled={loading}
                className="mt-2 text-sm font-medium text-primary-500 hover:text-primary-600 transition-colors"
              >
                Use SMS code instead
              </button>
            </div>
          )}

          {/* ---- Step: SMS Code Input ---- */}
          {(step === "sms-sent" || step === "sms-verify") && (
            <form onSubmit={handleSmsVerify} className="flex flex-col gap-4">
              <div className="text-center">
                <Icon
                  name="sms"
                  className="mx-auto mb-2 text-4xl text-primary-500"
                />
                <p className="text-sm text-text-secondary">
                  We sent a 6-digit code to{" "}
                  <span className="font-semibold text-text-primary">
                    {normalizedPhone}
                  </span>
                </p>
              </div>

              <div>
                <label
                  htmlFor="sms-code"
                  className="mb-1 block text-sm font-medium text-text-primary"
                >
                  Verification code
                </label>
                <input
                  id="sms-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  placeholder="000000"
                  value={smsCode}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "").slice(0, 6);
                    setSmsCode(value);
                  }}
                  disabled={loading}
                  required
                  className="w-full rounded-xl border border-[#f0e0e0] py-2.5 text-center text-lg font-semibold tracking-[0.3em] text-text-primary placeholder:text-text-muted placeholder:tracking-[0.3em] focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 focus:outline-none disabled:bg-gray-50 disabled:text-text-muted"
                />
                <p className="mt-1 text-xs text-text-muted">
                  Code expires in 10 minutes.
                </p>
              </div>

              <button
                type="submit"
                disabled={loading || smsCode.length < 6}
                className="flex items-center justify-center gap-2 rounded-xl bg-primary-500 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600 focus:ring-2 focus:ring-primary-500/20 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
              >
                {step === "sms-verify" ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                    Verifying…
                  </>
                ) : (
                  "Verify Code"
                )}
              </button>

              <div className="flex items-center justify-between text-sm">
                <button
                  type="button"
                  onClick={handleResendSms}
                  disabled={loading}
                  className="font-medium text-primary-500 hover:text-primary-600 transition-colors disabled:opacity-50"
                >
                  Resend code
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setStep("phone");
                    setSmsCode("");
                    setError(null);
                  }}
                  className="font-medium text-text-secondary hover:text-text-primary transition-colors"
                >
                  Use a different number
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Registration prompt */}
        <p className="mt-6 text-center text-sm text-text-secondary">
          Don&apos;t have an account?{" "}
          <a
            href="/register"
            className="font-semibold text-primary-500 hover:text-primary-600 transition-colors"
          >
            Get verified
          </a>
        </p>

        {/* Footer links */}
        <div className="mt-8 flex items-center justify-center gap-4 text-xs text-text-muted">
          <a
            href="/privacy"
            className="hover:text-text-secondary transition-colors"
          >
            Privacy Policy
          </a>
          <span>·</span>
          <a
            href="/terms"
            className="hover:text-text-secondary transition-colors"
          >
            Terms of Service
          </a>
          <span>·</span>
          <a
            href="/support"
            className="hover:text-text-secondary transition-colors"
          >
            Support
          </a>
        </div>
      </div>
    </div>
  );
}
